// Ribbon.tsx (or inline)
import React from "react";

type RibbonProps = {
  label: string;
  size?: "md" | "lg";
  glossy?: boolean; // controls band gloss (text is always flat)
};

export const Ribbon: React.FC<RibbonProps> = ({ label, size = "lg", glossy = true }) => {
  const W = 360;
  const H = 140;
  const fontSize = size === "lg" ? 24 : 22; // slightly smaller than before to avoid clipping

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="auto" preserveAspectRatio="xMidYMid meet" aria-label={label}>
      <defs>
        <linearGradient id="rband" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ff3b2e" />
          <stop offset="100%" stopColor="#a30000" />
        </linearGradient>

        {/* optional subtle band gloss */}
        <linearGradient id="shine" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(255,255,255,.28)" />
          <stop offset="55%" stopColor="rgba(255,255,255,.08)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </linearGradient>

        <filter id="outerShadow" x="-20%" y="-50%" width="140%" height="200%">
          <feDropShadow dx="0" dy="3" stdDeviation="4" floodOpacity="0.35" />
        </filter>

        {/* tiny legibility shadow just for text */}
        <filter id="textShadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="1" stdDeviation="0.7" floodOpacity="0.35" />
        </filter>

        {/* clip to band so long labels don’t spill; no stretching */}
        <clipPath id="bandClip">
          <path
            d="
              M30,60
              h300
              a24,24 0 0 1 24,24
              v12
              a24,24 0 0 1 -24,24
              h-300
              a24,24 0 0 1 -24,-24
              v-12
              a24,24 0 0 1 24,-24
              z
            "
          />
        </clipPath>
      </defs>

      {/* tails */}
      <g opacity="0.98" filter="url(#outerShadow)">
        <path d="M55,90 L20,78 Q10,74 20,66 L85,50 L100,62 L72,74 Z" fill="#b10e0e" />
        <path d="M305,90 L340,78 Q350,74 340,66 L275,50 L260,62 L288,74 Z" fill="#b10e0e" />
      </g>

      {/* band */}
      <g filter="url(#outerShadow)">
        <path
          d="
            M30,60
            h300
            a24,24 0 0 1 24,24
            v12
            a24,24 0 0 1 -24,24
            h-300
            a24,24 0 0 1 -24,-24
            v-12
            a24,24 0 0 1 24,-24
            z
          "
          fill="url(#rband)"
          stroke="#7d0000"
          strokeWidth="3"
        />
        {glossy && (
          <path
            d="
              M32,62
              h296
              a22,22 0 0 1 22,22
              v6
              a22,22 0 0 1 -22,22
              h-296
              a22,22 0 0 1 -22,-22
              v-6
              a22,22 0 0 1 22,-22
              z
            "
            fill="url(#shine)"
          />
        )}
      </g>

      {/* label (flat fill, no outline) */}
      <g clipPath="url(#bandClip)">
        <text
          x={W / 2}
          y={96}
          textAnchor="middle"
          dominantBaseline="middle"
          fontFamily="Corbel, Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif"
          fontWeight={800}
          fontSize={fontSize}
          fill="#ffea77"
          filter="url(#textShadow)"
        >
          {label}
        </text>
      </g>
    </svg>
  );
};
